import React, { Suspense } from 'react';
import { InMemoryCache } from '@apollo/client/cache';
import { datadogRum } from '@datadog/browser-rum';
import { ThemeProvider as LegacyMaterialUiThemeProvider } from '@mui/material/styles';
import { createRoot } from 'react-dom/client';
import i18n from './config/i18n';
import Icons from './config/icons';
import DataContextProvider from './contexts/data-context';
import FeatureContextProvider from './contexts/feature-context';
import LayoutContextProvider from './contexts/layout-context';
import LocalStateContextProvider from './contexts/local-state-context';
import AuthContextProvider from './contexts/user-context';
import App from './views/App';
import Loading from './views/components/core/loading';
import { LoadingProvider } from '@contexts-clean/loader-context';
import { ReadOnlyContextProvider } from '@contexts-clean/read-only-context';
import { UnsavedChangesProvider } from '@contexts-clean/unsaved-changes-context';
import OpraTheme from 'config/theme';

// Initialise our various libraries
const { environment } = window;
if (environment !== 'LOCAL') {
    datadogRum.init({
        applicationId: '9afda0bb-6e00-4083-b987-e4e3801f34d4',
        clientToken: 'pub7179cad0b2464215f8a5a899c42650fa',
        site: 'datadoghq.eu',
        service: 'OPRA 5',
        env: environment as string | undefined,
        version: import.meta.env.VITE_VERSION,
        sessionSampleRate: 100,
        trackUserInteractions: false,
    });
}
Icons.init();
i18n.init();

(window as any).setLanguage = i18n.setLanguage;

// Create the cache for apollo data, persist it to session storage by passing it into DataContextProvider
const cache = new InMemoryCache();

createRoot(document.getElementById('root')!).render(
    <Suspense fallback={<Loading />}>
        <DataContextProvider cache={cache}>
            <AuthContextProvider>
                <FeatureContextProvider>
                    <LocalStateContextProvider>
                        <LayoutContextProvider>
                            <LoadingProvider>
                                <LegacyMaterialUiThemeProvider theme={OpraTheme}>
                                    <UnsavedChangesProvider>
                                        <ReadOnlyContextProvider>
                                            <App />
                                        </ReadOnlyContextProvider>
                                    </UnsavedChangesProvider>
                                </LegacyMaterialUiThemeProvider>
                            </LoadingProvider>
                        </LayoutContextProvider>
                    </LocalStateContextProvider>
                </FeatureContextProvider>
            </AuthContextProvider>
        </DataContextProvider>
    </Suspense>,
);
